import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'

import { useAppSelector } from '../../store'
import { useSocketEvent, useSocketConnection } from './use-socket'
import type { CheckRunStatusChangedPayload } from './types'

interface UseCheckRunEventsOptions {
  enabled: boolean
  showToasts?: boolean
  onEvent?: (payload: CheckRunStatusChangedPayload) => void
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Hook to handle check-run:status-changed events for real-time updates
 */
export function useCheckRunEvents({
  enabled,
  showToasts = false,
  onEvent
}: UseCheckRunEventsOptions) {
  const queryClient = useQueryClient()
  const user = useAppSelector(state => state.app.user)
  const { isConnected } = useSocketConnection()

  const userEmail = user?.email

  const handleEvent = useCallback(
    (payload: CheckRunStatusChangedPayload) => {
      if (!enabled) {
        return
      }

      void queryClient.invalidateQueries({ queryKey: ['release-payment-files'] })
      void queryClient.invalidateQueries({ queryKey: ['check-runs', 'meta', 'totals'] })
      void queryClient.invalidateQueries({ queryKey: ['check-run-totals'] })
      void queryClient.invalidateQueries({ queryKey: ['summary'] })

      if (
        showToasts &&
        payload.trigger === 'manual' &&
        payload.triggeredBy?.email === userEmail &&
        (payload.action === 'rerun' || payload.action === 'rebuild')
      ) {
        const isSuccess = payload.status === 5
        const action = payload.action ? capitalize(payload.action) : 'Action'

        notification[isSuccess ? 'success' : 'error']({
          message: `${action} ${isSuccess ? 'completed' : 'failed'}`,
          description: `${payload.groupNumber} - ${payload.groupName}`,
          duration: 4
        })
      }

      onEvent?.(payload)
    },
    [enabled, showToasts, queryClient, userEmail, onEvent]
  )

  useSocketEvent('check-run:status-changed', handleEvent)

  return { isConnected }
}
